/**
 * Utility function to check if the tenant is logged in by verifying
 * required storage items are present
 * @returns {boolean} True if tenant is logged in, false otherwise
 */
export function isTenantLoggedIn(): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }

  const tenantId = localStorage.getItem('tenantId');

  if (!tenantId) {
    console.warn('Tenant not logged in: Missing tenant ID');
    return false;
  }

  return true;
}

/**
 * Utility function to check if Keycloak authentication is active.
 * This checks for the presence of OAuth tokens in storage.
 * @returns {boolean} True if Keycloak auth is active, false otherwise
 */
export function isKeycloakAuthenticated(): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }

  const accessToken = localStorage.getItem('access_token');
  return !!accessToken;
}

/**
 * Utility function to check if the user has the required permissions.
 * It checks the 'userPermissions' item in localStorage, which should be a JSON array of permission strings.
 * @param {string[]} permissionsNeeded - The list of permissions to check against the user's permissions
 * @returns {boolean} True if the user has all required permissions, false otherwise
 */
export function isUserAllowed(permissionsNeeded: string[]): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }

  const userPermissionsStr = localStorage.getItem('userPermissions');
  if (!userPermissionsStr) {
    return false;
  }

  const userPermissions = JSON.parse(userPermissionsStr) as Record<string, string[]>;
  const tenantId = localStorage.getItem('tenantId');

  if (!tenantId) {
    return false;
  }

  // Merge permissions from the specific tenant and the "*" wildcard tenant
  const tenantPermissions = userPermissions[tenantId] ?? [];
  const wildcardPermissions = userPermissions['*'] ?? [];
  const effectivePermissions = [...tenantPermissions, ...wildcardPermissions];

  if (effectivePermissions.includes('*')) {
    return true;
  }

  return permissionsNeeded.every(permission => effectivePermissions.includes(permission));
}

/**
 * Utility function to check if the user has admin permissions for all tenants.
 * Admin users have the wildcard permission: {"*": ["*"]}
 * @returns {boolean} True if the user is an admin, false otherwise
 */
export function isAdminUser(): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }

  const userPermissionsStr = localStorage.getItem('userPermissions');
  if (!userPermissionsStr) {
    return false;
  }

  try {
    const userPermissions = JSON.parse(userPermissionsStr) as Record<string, string[]>;
    return Array.isArray(userPermissions['*']) && userPermissions['*'].includes('*');
  } catch {
    return false;
  }
}